/**
 * @file SearchBar.js
 * @module src/js/components/SearchBar
 * @description
 * **A reusable search bar component for filtering data lists in the EDALab frontend.**
 * - Accepts data and filter keys as props to perform case-insensitive search.
 * - Updates the filtered data state in the parent component.
 * - Resets pagination to the first page upon search keyword change.
 * - Adapts width based on device type (mobile or desktop).
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: Added support for filtering date fields with '-99' suffix. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-01: Trim search keyword before filtering. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useRef, useState } from "react";
import getDevice from "../functions/getDevice";
import { Search, X } from "lucide-react";

export default function SearchBar({
    data,
    setFilteredData,
    setCurrentPage,
    setCurrentFirstPage,
    currentCategory = null,
    filterKeys = null
}) {
    const [searchKeyword, setSearchKeyword] = useState('');
    const inputRef = useRef(null);
    const { isMobile } = getDevice();

    useEffect(() => {
        if (!data) {
            setFilteredData([]);
            return;
        }
        if (searchKeyword === '') {
            setFilteredData(data);
            return;
        }

        const searchKeywordLowerTrimmed = searchKeyword.toLowerCase().trim();

        const filtered = data.filter((item) => {
            if (filterKeys) {
                return filterKeys.some(field => {
                    const value = ((field === 'date' || field === 'Date') && item[field].includes('-99')) ?
                        item[field].split('-')[0] : item[field];
                    return (typeof value === 'string' || typeof value === 'number') &&
                        value.toString().toLowerCase().includes(searchKeywordLowerTrimmed);
                });
            } else {
                return Object.values(item).some(value =>
                    (typeof value === 'string' || typeof value === 'number') &&
                    value.toString().toLowerCase().includes(searchKeywordLowerTrimmed)
                );
            }
        });

        setFilteredData(filtered);
    }, [searchKeyword, data, setFilteredData, filterKeys]);

    useEffect(() => {
        setCurrentPage(1);
        setCurrentFirstPage(1);
    }, [searchKeyword, setCurrentPage, setCurrentFirstPage]);

    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: isMobile ? '100%' : '60%' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        verticalAlign: 'middle',
                        gap: '10px',
                    }}
                >
                    <Search size={22} />
                    <input
                        type="text"
                        ref={inputRef}
                        placeholder={`Search${currentCategory !== null ? ` in ${currentCategory}` : ''}`}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                    />
                    {searchKeyword !== '' && (
                        <span
                            onClick={() => {
                                setSearchKeyword('');
                                if (inputRef.current) {
                                    inputRef.current.focus(); // focus on input
                                }
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <X size={22} />
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}